"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, ChevronDown } from "lucide-react";
import { navigation } from "@/data/navigation";
import MegaMenu from "./MegaMenu";
import MobileNav from "./MobileNav";
import Button from "@/components/ui/Button";
import ThemeToggle from "@/components/ui/ThemeToggle";
import Logo from "@/components/ui/Logo";
import Container from "@/components/ui/Container";
import { APP_LOGIN_URL } from "@/lib/site-config";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu]     = useState<string | null>(null);
  const [scrolled, setScrolled]     = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Single source of truth for the scroll-linked backdrop colors. Reads the
  // current theme on every call so it works for both scroll and theme-toggle
  // events. Pulled out of the GSAP onUpdate so the theme observer can call
  // it directly without waiting for the next scroll tick.
  const paintHeader = useRef((progress: number) => {
    if (!headerRef.current) return;
    const p = progress;
    // Cap blur on small screens — heavy filter on low-end Android hurts scroll FPS.
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
    const blurMax = isMobile ? 6 : 12;
    const blur = 4 + p * blurMax;
    const isLight = document.documentElement.dataset.theme === "light";
    const borderOp = p * (isLight ? 0.10 : 0.08);
    const bgOp = isLight ? 0.55 + p * 0.4 : 0.4 + p * 0.5;
    const borderRGB = isLight ? "13,13,16" : "255,255,255";
    const bgRGB = isLight ? "250,249,247" : "10,10,10";
    headerRef.current.style.backdropFilter = `blur(${blur}px)`;
    headerRef.current.style.setProperty("-webkit-backdrop-filter", `blur(${blur}px)`);
    headerRef.current.style.borderBottomColor = `rgba(${borderRGB},${borderOp})`;
    headerRef.current.style.backgroundColor = `rgba(${bgRGB},${bgOp})`;
  });

  // Re-paint header on theme toggle. ScrollTrigger.update() alone won't
  // re-fire onUpdate when scroll progress hasn't changed — so we read the
  // current scrollY directly and recompute. Also re-paints on next frame
  // to catch CSS variable propagation.
  useEffect(() => {
    const root = document.documentElement;
    const obs = new MutationObserver(() => {
      const total = (document.documentElement.scrollHeight - window.innerHeight) || 1;
      const distance = Math.min(window.scrollY, 200);
      const progress = Math.min(1, distance / 200);
      // Two-frame delay: first frame may still see the old data-theme inside
      // ScrollTrigger's cached scope; second frame is guaranteed post-flip.
      requestAnimationFrame(() => {
        paintHeader.current(progress);
        requestAnimationFrame(() => paintHeader.current(progress));
      });
      void total;
    });
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  // ── GSAP: Header entrance on page load ─────────────────────────────────
  // GSAP is dynamic-imported so it lives in a separate chunk, keeping the
  // critical bundle small (~60KB off the main thread until idle).
  useEffect(() => {
    if (!headerRef.current) return;
    let cleanup: (() => void) | undefined;

    import("@/lib/gsap").then(({ gsap, ScrollTrigger }) => {
      if (!headerRef.current) return;
      const ctx = gsap.context(() => {
        const mm = gsap.matchMedia();

        // Reduced motion: skip the entrance animation but still reveal the
        // elements that start with inline `visibility: hidden` (logo + CTAs),
        // otherwise they'd stay invisible forever for these users.
        mm.add("(prefers-reduced-motion: reduce)", () => {
          gsap.set(".header-logo, .header-cta", { visibility: "visible" });
        });

        mm.add("(prefers-reduced-motion: no-preference)", () => {
          const tl = gsap.timeline({ delay: 0.1 });
          tl.from(".header-logo", {
            x: -20,
            autoAlpha: 0,
            duration: 0.5,
            ease: "power3.out",
          })
            .from(
              ".header-nav-item",
              {
                y: -10,
                autoAlpha: 0,
                stagger: 0.05,
                duration: 0.4,
                ease: "power2.out",
              },
              "-=0.3",
            )
            .from(
              ".header-cta",
              {
                scale: 0.9,
                autoAlpha: 0,
                stagger: 0.08,
                duration: 0.4,
                ease: "back.out(1.4)",
              },
              "-=0.2",
            );

          // Dynamic header backdrop — blur + border opacity tied to scroll.
          // Delegates to paintHeader so theme toggles share the exact same logic.
          ScrollTrigger.create({
            trigger: document.documentElement,
            start: "top top",
            end: "200px top",
            onUpdate: (self) => paintHeader.current(self.progress),
          });
        });
      }, headerRef);

      cleanup = () => ctx.revert();
    });

    return () => {
      cleanup?.();
    };
  }, []);

  // Close mega menu on Escape
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenMenu(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openMenu]);

  const openGroup  = (label: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const megaKeys = Object.keys(navigation.megaMenu);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <>
      <header
        ref={headerRef}
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          scrolled
            ? "border-b border-border-subtle bg-bg-base/90 backdrop-blur-md"
            : "border-b border-transparent bg-bg-base/40 backdrop-blur-sm"
        }`}
      >
        <Container>
          <nav className="flex h-16 items-center justify-between gap-4">
            {/* Logo */}
            <Link href="/" className="header-logo shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded-lg" style={{ visibility: "hidden" }}>
              <Logo priority />
            </Link>

            {/* Desktop nav — a single mega panel anchored to this group (not
                per-item) so wide panels never overflow the viewport edge. */}
            <div
              className="relative hidden lg:block"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              onFocus={cancelClose}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) scheduleClose();
              }}
            >
              <ul className="flex items-center gap-0.5">
                {navigation.mainLinks.map((link) => {
                  const hasMega = megaKeys.includes(link.label);
                  const active = !hasMega && isActive(link.href);

                  return (
                    <li
                      key={link.label}
                      className="header-nav-item"
                      onMouseEnter={() => (hasMega ? openGroup(link.label) : setOpenMenu(null))}
                    >
                      {hasMega ? (
                        <button
                          onFocus={() => openGroup(link.label)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (openMenu === link.label) setOpenMenu(null); else openGroup(link.label);
                            }
                            if (e.key === "Escape") setOpenMenu(null);
                          }}
                          aria-expanded={openMenu === link.label}
                          aria-haspopup="true"
                          className="nav-hover-underline flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-text-secondary transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60"
                        >
                          {link.label}
                          <ChevronDown
                            size={14}
                            className={`transition-transform duration-200 ${openMenu === link.label ? "rotate-180" : ""}`}
                          />
                        </button>
                      ) : (
                        <Link
                          href={link.href}
                          className={`nav-hover-underline relative block rounded-lg px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60 ${
                            active
                              ? "text-white"
                              : "text-text-secondary hover:text-white"
                          }`}
                        >
                          {link.label}
                          {active && (
                            <span
                              aria-hidden
                              className="absolute bottom-0 left-3 right-3 h-px rounded-full bg-brand-purple"
                            />
                          )}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>

              {openMenu && megaKeys.includes(openMenu) && (
                <MegaMenu group={navigation.megaMenu[openMenu]} isOpen />
              )}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden items-center gap-3 lg:flex">
              <Link
                href="/contact-us"
                data-cta="book-a-demo"
                data-cta-location="header"
                className="header-cta text-sm text-text-secondary transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60 rounded-md px-2 py-1"
                style={{ visibility: "hidden" }}
              >
                Book a Demo
              </Link>
              <span className="header-cta" style={{ visibility: "hidden" }}>
                <Button
                  href={APP_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="soft"
                  size="sm"
                  data-cta="sign-in"
                  data-cta-location="header"
                >
                  Open App
                </Button>
              </span>
              <span className="header-cta">
                <ThemeToggle />
              </span>
            </div>

            {/* Mobile cluster: Open App pill + hamburger (thumb-zone, right edge) */}
            <div className="flex items-center gap-2 lg:hidden">
              <Button
                href={APP_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="soft"
                size="sm"
                data-cta="sign-in"
                data-cta-location="header-mobile"
              >
                Open App
              </Button>
              <button
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav-drawer"
                className="rounded-lg p-2 text-text-secondary hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <Menu size={20} />
              </button>
            </div>
          </nav>
        </Container>
      </header>

      {mobileOpen && <MobileNav onClose={() => setMobileOpen(false)} />}
    </>
  );
}
